import { FC, useEffect, useState } from 'react';
import useCartModelDisplay from '../../../../../hooks/useCartModelDisplay';
import Product from '../../../../../@types/Product';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Box, Stack, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import assets from '../../../../../assets';
import { AccessoriesOptionalXtremeMats } from '../../../../../data/products/Carts/Shared/Accessories';
import useCurrencyConverter from '../../../../../hooks/useCurrencyConverter';

interface CartProductAccessoriesProps {
  accessoriesIncluded: boolean,
  retailConfigurator: boolean,
  hideAllPrices: boolean,
}

const CartProductAccessories: FC<CartProductAccessoriesProps> = ({ accessoriesIncluded, retailConfigurator, hideAllPrices }) => {
  const { setValue, getValues } = useFormContext();
  const { chassisType, liftKit, bodyColor } = useCartModelDisplay();
  const [accessories, setAccessories] = useState<Product[]>([]);
  const selectedAccessories = useWatch({
    name: accessoriesIncluded
      ? 'includedAccessories'
      : 'optionalAccessories'
  });
  const { control } = useFormContext();
  const { } = useFieldArray({
    control,
    name: accessoriesIncluded
      ? 'includedAccessories'
      : 'optionalAccessories'
  });

  useEffect(() => {
    if (accessoriesIncluded) {
      setAccessories(liftKit?.sku === 'L'
        ? chassisType?.includedAccessoriesLifted ?? []
        : chassisType?.includedAccessories ?? []
      );
      // Clear current list of included accessories
      // Put all avaible accessories by type into included accessories

      setValue('includedAccessories', liftKit?.sku === 'L'
        ? chassisType?.includedAccessoriesLifted ?? []
        : chassisType?.includedAccessories ?? []);
    } else {
      const colorMatchedXtremeMats = AccessoriesOptionalXtremeMats.filter((product) => {
        if (Array.isArray(product.refId)){ 
          return product.refId.some((refId) => refId === bodyColor?.sku);
        } else {  
          return product.refId === bodyColor?.sku;
        }
      })

      /* 
        Need to get current list of optional accessories and make sure there
        are not accessories that are only avaible to lifted or non-lifted currently
        set.  We still want to retain all other accessories selected between
        lifted and non-lifted
      */ 
      
      const currentOptionalAccessories: Product[] = getValues('optionalAccessories');
      
      // filter out accessories

      const currentOptionalAccessoriesFilter = currentOptionalAccessories.filter((product) => {
        if (product?.refId) {
          if (Array.isArray(product.refId)) {
            return product.refId.some((refId) => refId === liftKit?.sku);
          } else {
            return product.refId === liftKit?.sku
          }
        } else {
          return product;
        }
      });

      setValue('optionalAccessories', currentOptionalAccessoriesFilter);

      setAccessories(liftKit?.sku === 'L'
        ? [...chassisType?.optionalAccessoriesLifted ?? [], ...colorMatchedXtremeMats]
        : [...chassisType?.optionalAccessories ?? [], ...colorMatchedXtremeMats]
      );
    }
  }, [
    bodyColor,
    liftKit,
    chassisType,
    AccessoriesOptionalXtremeMats,
    setAccessories,
    getValues
  ]);

  const removeAddAccessory = (accessory: Product) => {
    if (selectedAccessories?.some((acc: Product) => acc.id === accessory.id)) {
      // remove
      setValue(`${accessoriesIncluded
        ? 'includedAccessories'
        : 'optionalAccessories'
        }`, selectedAccessories?.filter((acc: Product) => acc.id !== accessory.id));
    } else {
      // add
      setValue(`${accessoriesIncluded
        ? 'includedAccessories'
        : 'optionalAccessories'
        }`, [...selectedAccessories, accessory]);
    }
  }

  return (
    <Stack
      width="100%"
      direction="row"
      useFlexGap
      flexWrap="wrap"
      spacing={4}>
      {accessories.map((accessory, index) => {
        const selected = selectedAccessories?.some((acc: Product) => acc.id === accessory.id);
        return (
          <AccessorySelect
            key={index}
            included={accessoriesIncluded}
            accessory={accessory}
            selected={selected}
            retailConfigurator={retailConfigurator}
            displayPrice={!accessoriesIncluded && !hideAllPrices}
            removeAddAccessory={removeAddAccessory}
            disabled={accessoriesIncluded} />
        )
      })}
    </Stack>
  );
}

interface AccessorySelectProps {
  accessory: Product,
  included: boolean,
  selected: boolean,
  removeAddAccessory: (accessory: Product) => void;
  disabled?: boolean,
  displayPrice?: boolean,
  imageWidth?: number,
  retailConfigurator: boolean
}

const AccessorySelect = ({
  accessory,
  included,
  selected,
  removeAddAccessory,
  disabled = false,
  displayPrice = true,
  imageWidth = 175,
  retailConfigurator
}: AccessorySelectProps) => {
  const { convertCurrency } = useCurrencyConverter(); 
  return (
    <Stack
      maxWidth="205px"
      minWidth="205px"
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{
        padding: '10px',
        borderRadius: '6px',
        cursor: disabled ? 'arrow' : 'pointer',
        ...((selected) && {
          border: '4px solid #3D4449',
          borderImage: 'linear-gradient(99deg, #931216, #E20B23, #FF8230) 1'

        }),
        ...((!selected)) && {
          border: '4px solid transparent'
        }
      }}
      onClick={() =>
        disabled
          ? null
          : removeAddAccessory(accessory)}>
      {accessory.img
        ? (
          <img draggable="false"
            src={accessory.img}
            alt={`${accessory.name}`}
            width={`${imageWidth}px`} />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: `${imageWidth}px`,
              minHeight: `${imageWidth}px`,
              backgroundColor: '#D9D9D9'
            }}>
            <img src={assets.images.missingImage} alt='missing' />
          </Box>
        )
      }
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        position="relative">
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          marginBottom="35px"
          minHeight="48px"
          spacing={0.5}>
          <Typography
            variant="secondaryFont"
            fontWeight={500}>
            {accessory.name}
          </Typography>
          {displayPrice &&
            <Typography variant="secondaryFont">
              ({convertCurrency(
                retailConfigurator
                  ? accessory.retailPrice ?? accessory.price
                  : accessory.price)
              })
            </Typography>
          }
        </Stack>
        {!included &&
          <CheckCircleRoundedIcon sx={{
            display: 'none',
            color: '#cb2a20',
            backgroundColor: 'white',
            position: 'absolute',
            bottom: '-34px',
            width: '40px',
            height: '40px',
            ...((selected)) && {
              display: 'inline-block !important'
            },
          }} />
        }
        {included &&
          <Typography
            variant="secondaryFont"
            sx={{
              paddingLeft: '6px',
              paddingRight: '6px',
              display: 'none',
              color: '#cb2a20',
              textTransform: 'uppercase',
              fontWeight: 'bold',
              backgroundColor: 'white',
              position: 'absolute',
              bottom: '-22px',
              ...((selected)) && {
                display: 'inline-block !important'
              },
            }}>
            Included
          </Typography>
        }
      </Stack>
    </Stack>
  )
}

export default CartProductAccessories;
